import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { Subscription } from "rxjs";
import {
  CatalogCategoryViewModel,
  ProductViewModel,
  SearchQueryModel,
  SortTypeEnum,
} from "src/domain/client";
import IProductListReport from "src/domain/event-emitters/product-list-report";
import { ActivityLogService } from "src/services/activity-log.service";
import { CatalogCategoryService } from "src/services/catalog-category.service";
import { ChannelRequestService } from "src/services/channel-request.service";
import {
  IReloadSearchParameters,
  ProductListService,
} from "src/services/product-list.service";
import { ProductService } from "src/services/product.service";
import { SearchService } from "src/services/search.service";
import { ThemeService } from "src/services/theme.service";

@Component({
  selector: "vw-product-list",
  templateUrl: "./product-list.component.html",
})
export class ProductListComponent implements OnInit, OnDestroy {
  @Input() categoryId?: number;
  @Input() query?: string;
  @Input() noProductsMessage?: string;
  @Input() logSearch?: boolean;

  @Output() reportNumberOfProductIDs$ = new EventEmitter<IProductListReport>();

  searchLoading = false;
  productsLoading = false;
  error = false;
  productIDs: number[] = [];
  pageSize = 24;
  page = 0;
  products: ProductViewModel[] = [];
  categories: CatalogCategoryViewModel[] = [];
  showLoadMore = false;

  private searchParametersSubscription?: Subscription;

  constructor(
    public themeService: ThemeService,
    private searchService: SearchService,
    private productService: ProductService,
    private productListService: ProductListService,
    private catalogCategoryService: CatalogCategoryService,
    private channelRequestService: ChannelRequestService,
    private activityLogService: ActivityLogService,
  ) {}

  ngOnInit(): void {
    if (this.query || this.categoryId) {
      this.search(this.query, SortTypeEnum.AlphabeticalAscending);
    }

    this.searchParametersSubscription =
      this.productListService.reloadSearch$.subscribe(
        (params: IReloadSearchParameters) => {
          this.search(
            params.query,
            this.productListService.sortType,
            params.emit,
          );
        },
      );
  }

  ngOnDestroy(): void {
    this.searchParametersSubscription?.unsubscribe();
  }

  getProducts(): void {
    this.page++;

    const productIDs = this.productIDs.slice(
      this.page * this.pageSize - this.pageSize,
      this.page * this.pageSize,
    );

    this.productsLoading = true;
    this.error = false;

    this.productService
      .getProducts(productIDs)
      .then((products) => {
        this.products = [...this.products, ...products];

        this.showLoadMore = this.productIDs.length > this.products.length;
      })
      .catch(() => {
        this.error = true;
      })
      .finally(() => {
        this.productsLoading = false;
      });
  }

  //////////

  private search(
    query: string | undefined | null,
    sortType: SortTypeEnum,
    emit = true,
  ): void {
    const requestModel = new SearchQueryModel({
      query: query || "",
      categoryId: this.categoryId,
      includeProducts: true,
      includeCategories: !!this.query,
      usePassingControl: true,
      sortType,
    });

    this.page = 0;
    this.productIDs = [];
    this.products = [];
    this.categories = [];
    this.searchLoading = true;
    this.error = false;
    this.showLoadMore = false;

    this.searchService
      .getSearchResult(requestModel)
      .then((searchResultModel) => {
        this.productIDs = searchResultModel.productIds ?? [];

        if (
          searchResultModel.productIds &&
          searchResultModel.productIds.length
        ) {
          this.getProducts();
        }

        if (
          searchResultModel.categoryIds &&
          searchResultModel.categoryIds.length
        ) {
          this.getCategories(searchResultModel.categoryIds);
        }

        if (
          this.channelRequestService.channelRequest?.channelRequestId != null &&
          this.logSearch &&
          requestModel.query
        ) {
          this.activityLogService
            .logSearch(
              this.channelRequestService.channelRequest.channelRequestId,
              requestModel.query,
            )
            .catch(() => {});
        }
      })
      .catch(() => {
        this.error = true;
      })
      .finally(() => {
        if (emit) {
          this.reportNumberOfProductIDs$.emit({
            categoryId: this.categoryId || undefined,
            numberOfProducts: this.productIDs.length,
          });
        }
        this.searchLoading = false;
      });
  }

  private getCategories(categoryIds: number[]): void {
    const channelRequestId =
      this.channelRequestService.channelRequest?.channelRequestId;
    this.catalogCategoryService
      .getCatalogCategoriesByIds(categoryIds, channelRequestId)
      .then((categories) => (this.categories = categories))
      .catch(() => {});
  }
}
